/* eslint-disable react/no-array-index-key */
import { useCallback, useMemo, useState } from 'react';

import { AgentsListBodySectionProps } from './AgentsListBody.decl';
import { ColumnFieldAvailability } from './ColumnFieldAvailability.container';
import { ColumnFieldName } from './ColumnFieldName.container';
import { ColumnFieldRole } from './ColumnFieldRole.container';

import { OrderDirection, TableColumn } from '@aircall/tractor-v2';
import { AgentDeleteConfirmationModal } from '@components/AgentDeleteConfirmationModal/AgentDeleteConfirmationModal';
import { USER_DETAIL_ROUTE_TAB } from '@constants/routes.constants';
import { LoadMoreTable } from '@dashboard/library';
import { SearchableAgentsSortableFields } from '@generated/globalTypes';
import { UserMinimalFragment, UserMinimalFragment_roles } from '@generated/UserMinimalFragment';
import { canDeleteUser } from '@helpers/permissions.helpers';
import { reverseOrderDirection } from '@helpers/sort.helpers';
import { useDeleteUser } from '@hooks/useDeleteUser';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useNavigateWithParamsReplace } from '@hooks/useNavigateWithParamsReplace';
import { usePermissions } from '@hooks/usePermissions';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useTranslation } from 'react-i18next';

export function AgentsListBodySection({
  agentsList,
  hasMore,
  loading,
  loadingMore,
  onLoadMore,
  onOrderChange,
  order,
}: AgentsListBodySectionProps) {
  const navigate = useNavigateWithParamsReplace();
  const { t } = useTranslation();
  const { roles: currentUserRoles } = usePermissions();
  const {
    currentUser: { ID: currentUserId },
  } = useGlobalData();

  const [isDeletable, setIsDeletable] = useState(true);
  const [idToDelete, setIdToDelete] = useState('');
  const [pictureURLToDelete, setPictureURLToDelete] = useState<string | null>(null);
  const [firstNameToDelete, setFirstNameToDelete] = useState('');
  const [lastNameToDelete, setLastNameToDelete] = useState('');
  const [isActiveToDelete, setIsActiveToDelete] = useState(true);
  const [rolesToDelete, setRolesToDelete] = useState<UserMinimalFragment_roles[]>([]);

  const [isModalOpen, _, onClick, onCancelClick] = useToggle();
  const { isDeleteLoading, deleteError, onSubmit } = useDeleteUser({
    userId: parseInt(idToDelete),
    onCompleted: onCancelClick,
  });

  const columns: TableColumn<UserMinimalFragment>[] = useMemo(
    () => [
      {
        id: SearchableAgentsSortableFields.firstName,
        label: t('users.table.name.label'),
        renderer: (props) => <ColumnFieldName {...props} />,
      },
      {
        id: 'availability',
        label: t('users.table.availability.label'),
        renderer: (props) => <ColumnFieldAvailability {...props} />,
        sortable: false,
      },
      {
        id: SearchableAgentsSortableFields.role,
        label: t('users.table.role.label'),
        renderer: (props) => <ColumnFieldRole {...props} />,
      },
    ],
    [t]
  );

  const handleEditUser = useCallback(
    ({ ID }: UserMinimalFragment) =>
      navigate(USER_DETAIL_ROUTE_TAB, { userId: ID, tab: 'general' }),
    [navigate]
  );

  const handleDeleteModal = useCallback(
    ({ firstName, ID, isActive, lastName, pictureURL, roles }: UserMinimalFragment) => {
      const displayDelete = canDeleteUser(currentUserId, currentUserRoles, ID, roles);

      setIsDeletable(displayDelete);
      setIdToDelete(ID);
      setPictureURLToDelete(pictureURL);
      setFirstNameToDelete(firstName);
      setLastNameToDelete(lastName);
      setRolesToDelete(roles);
      setIsActiveToDelete(isActive);
      onClick();
    },
    [currentUserId, currentUserRoles, onClick]
  );

  const handleOrderChange = useCallback(
    (field) => {
      const isSameField = field === order.field;
      const direction = isSameField ? reverseOrderDirection(order.direction) : OrderDirection.Asc;

      onOrderChange(field, direction);
    },
    [onOrderChange, order.direction, order.field]
  );

  const rowActionOptions = useMemo(
    () => [
      { onExecute: handleEditUser, label: t('users.table.row_action.edit') },
      { onExecute: handleDeleteModal, label: t('users.table.row_action.delete') },
    ],
    [handleEditUser, handleDeleteModal, t]
  );

  return (
    <>
      <LoadMoreTable<UserMinimalFragment>
        data-test='users-list-view-load-more-table'
        recordAttributeIdentifier='ID'
        buttonText={t('generic.load_more')}
        data={agentsList}
        columns={columns}
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        loadingText={t('generic.loading')}
        noDataMessage={t('users.no_result')}
        rowActions={rowActionOptions}
        onLoadMore={onLoadMore}
        onRowClick={handleEditUser}
        onOrderChange={handleOrderChange}
        order={order}
      />
      <AgentDeleteConfirmationModal
        deleteError={deleteError}
        isDeleteDisabled={!isDeletable}
        isDeleteLoading={isDeleteLoading}
        isModalOpen={isModalOpen}
        onCancelClick={onCancelClick}
        onDeleteClick={onSubmit}
        idToDelete={idToDelete}
        firstNameToDelete={firstNameToDelete}
        lastNameToDelete={lastNameToDelete}
        pictureURLToDelete={pictureURLToDelete}
        rolesToDelete={rolesToDelete}
        isActiveToDelete={isActiveToDelete}
      />
    </>
  );
}
